import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

export interface ToggleCardData {
  name: string;
  visible: boolean;
  selected?: boolean;
}

/**
 * Card-like UI component that displays a ToggleCardData item and provides a toggle switch and card click interactions.
 * This standalone component renders a single item, exposes its index, and emits structured events when the toggle changes or the card is clicked.
 * Key methods:
 *   - onToggleChange(): Called when the internal toggle is changed; emits the toggleChange event with { index, item }.
 *   - onCardClick(): Called when the card is clicked; emits the cardClick event with { index, item }.
 * Lifecycle hooks:
 *   - No explicit Angular lifecycle hooks are implemented on this component; it relies on simple input bindings and event emitters for interaction.
 * @component
 * @selector app-toggle-card
 * @example
 *   <app-toggle-card [item]="sampleItem" [index]="0" (toggleChange)="handleToggle($event)" (cardClick)="handleClick($event)"></app-toggle-card>
 * @input {ToggleCardData} item - The data object to render inside the card. Required; represents the content and state associated with this card.
 * @input {number} index - Numeric index or identifier for this card instance, included in emitted event payloads.
 * @output {EventEmitter<{index: number, item: ToggleCardData}>} toggleChange - Emitted when the toggle control is changed. Payload contains the index and the item.
 * @output {EventEmitter<{index: number, item: ToggleCardData}>} cardClick - Emitted when the card is clicked. Payload contains the index and the item.
 */
@Component({
  selector: 'app-toggle-card',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSlideToggleModule],
  templateUrl: './toggle-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./toggle-card.component.scss'],
})
export class ToggleCardComponent {
  @Input() item!: ToggleCardData;
  @Input() index!: number;
  @Output() toggleChange = new EventEmitter<{ index: number; item: ToggleCardData }>();
  @Output() cardClick = new EventEmitter<{ index: number; item: ToggleCardData }>();

  onToggleChange(): void {
    this.toggleChange.emit({
      index: this.index,
      item: this.item,
    });
  }

  onCardClick(): void {
    this.cardClick.emit({
      index: this.index,
      item: this.item,
    });
  }
}
